import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Typography,
  IconButton,
  Collapse,
  Tooltip,
  Chip,
  Button,
  Badge,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import ModeEditIcon from '@mui/icons-material/ModeEdit';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import CancelIcon from '@mui/icons-material/Cancel';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import AssignmentReturnIcon from '@mui/icons-material/AssignmentReturn';
import _ from 'lodash';
import React, { useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import dayjs from 'dayjs';
import PendingItemsModal from '../Modal/PendingItemsModal';
import RefundItemsModal from '../Modal/RefundItemsModal';

// badge สีฟ้าสำหรับแถวสรุปยอดรวม (ใต้หัวตาราง)
const TOTAL_BADGE_SX = {
  display: 'inline-flex',
  maxWidth: '100%',
  px: 1,
  py: 0.3,
  borderRadius: 5,
  bgcolor: '#dbeafe',
  border: '1px solid #93c5fd',
  color: '#1e3a8a',
  fontWeight: 700,
  fontSize: '0.65rem',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

// ตารางหลัก — table-layout: fixed + colgroup กำหนดสัดส่วนคอลัมน์เป็น % รวมกัน = 100% เสมอ
// ทำให้กรอบตารางกว้างคงที่ตาม TableContainer (ไม่มีทางล้น/ต้องเลื่อนแนวนอน หรือแย่งพื้นที่คอลัมน์อื่น
// แบบที่ table-layout: auto เคยทำ) — ปรับสัดส่วนได้ตรงนี้ที่เดียว
// 6 คอลัมน์ตัวเลขหลัก (Budget/Paid/Expected/AP Pending/รอเงินคืน/Grand Total) กว้างเท่ากันเป๊ะ (9%
// = ~104px ที่ maxWidth 1158) พอสำหรับตัวเลขหลักสิบล้าน เช่น "99,999,999.99" (13 ตัวอักษร) สบายๆ —
// Remaining กว้างกว่าเล็กน้อย (10%) เพราะอาจมีเครื่องหมายลบนำหน้าด้วย
const COL_WIDTHS = ['3%', '6%', '21%', '9%', '9%', '9%', '9%', '9%', '9%', '10%', '6%'];
const HEAD_TXT_SX = {
  color: '#fff',
  fontWeight: 700,
  letterSpacing: '0.02em',
  fontSize: '0.68rem',
  whiteSpace: 'nowrap',
};

// ref: เปิดให้ parent (เช่น ProjectPopup.js) สั่ง toggleAllRows() ได้จากปุ่มที่ย้ายไปวางไว้นอก component นี้
// hideToggleButton/onToggleStateChange: optional — ถ้า parent อยากวางปุ่ม "ดูค่าใช้จ่ายทั้งหมด" เองข้างนอก
// (ค่า default เดิมทุกอย่างเหมือนเดิมสำหรับ caller ที่ไม่ได้ส่ง prop พวกนี้มา เช่น ProjectDetail.js)
const BudgetTable = forwardRef(function BudgetTable({
  budget,
  handleEditBudget,
  handleDeleteBudget,
  onEditExpense,
  hideToggleButton,
  onToggleStateChange,
  // เรียกหลังเพิ่ม/แก้/รับคืน/ลบ รายการรอรับคืน — ให้หน้าที่ใช้ตารางโหลดงบใหม่ (response ของ API refund ไม่มี
  // refund_expenses ติดมา แถวรายการคืนในตารางจะค้างถ้าไม่โหลดใหม่)
  onRefundUpdated,
  uiLang = 'en',
}, ref) {
  const U = (th, en) => (uiLang === 'en' ? en : th);
  const [expandedRows, setExpandedRows] = useState({});
  const [expenseSortBy] = useState({}); // { [budgetId]: 'date' | 'price' }
  const [pendingModalId, setPendingModalId] = useState(null);
  const [refundModalId, setRefundModalId] = useState(null);

  const pendingModalBudget = _.find(budget, (b) => b.id === pendingModalId);
  const refundModalBudget = _.find(budget, (b) => b.id === refundModalId);

  const openPendingModal = (budgetId) => setPendingModalId(budgetId);
  const closePendingModal = () => setPendingModalId(null);

  const openRefundModal = (budgetId) => setRefundModalId(budgetId);
  const closeRefundModal = () => setRefundModalId(null);

  // แถวงบกดขยายได้ถ้ามีรายการเบิก หรือมีรายการเบิกรอรับคืน (refund_expenses) อย่างใดอย่างหนึ่ง
  const hasExpandableRows = (e) => !_.isEmpty(e?.expenses) || !_.isEmpty(e?.refund_expenses);

  const toggleRow = (id) => {
    setExpandedRows((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const allExpanded =
    !_.isEmpty(budget) &&
    budget.filter(hasExpandableRows).every((e) => !!expandedRows[e.id]);

  const toggleAllRows = () => {
    if (allExpanded) {
      setExpandedRows({});
    } else {
      const all = {};
      (budget || []).forEach((e) => { if (hasExpandableRows(e)) all[e.id] = true; });
      setExpandedRows(all);
    }
  };

  useImperativeHandle(ref, () => ({ toggleAllRows }), [toggleAllRows]);

  const toggleAllDisabled = _.isEmpty(budget) || budget.every((e) => !hasExpandableRows(e));

  useEffect(() => {
    onToggleStateChange?.({ allExpanded, disabled: toggleAllDisabled });
  }, [allExpanded, toggleAllDisabled]);

  const getSortedExpenses = (expenses, budgetId) => {
    const sortField = expenseSortBy[budgetId] || 'date';
    if (sortField === 'price') {
      return _.orderBy(expenses, ['price'], ['desc']);
    }
    return _.orderBy(expenses, [(e) => dayjs(e?.date).valueOf()], ['desc']);
  };

  const expenseStatusIcon = (status) => {
    if (status === 'SUCCESS')
      return (
        <Tooltip title="สำเร็จ">
          <CheckCircleIcon fontSize="small" sx={{ color: 'success.main', verticalAlign: 'middle' }} />
        </Tooltip>
      );
    if (status === 'APPROVE')
      return (
        <Tooltip title="อนุมัติแล้ว">
          <RadioButtonUncheckedIcon fontSize="small" sx={{ color: 'warning.main', verticalAlign: 'middle' }} />
        </Tooltip>
      );
    if (status === 'REJECT')
      return (
        <Tooltip title="ถูกปฏิเสธ">
          <CancelIcon fontSize="small" sx={{ color: 'error.main', verticalAlign: 'middle' }} />
        </Tooltip>
      );
    if (status === 'PREAPPROVE')
      return (
        <Tooltip title="รออนุมัติเบื้องต้น">
          <RadioButtonUncheckedIcon fontSize="small" sx={{ color: 'info.main', verticalAlign: 'middle' }} />
        </Tooltip>
      );
    return (
      <Tooltip title="รอดำเนินการ">
        <RadioButtonUncheckedIcon fontSize="small" sx={{ color: 'text.disabled', verticalAlign: 'middle' }} />
      </Tooltip>
    );
  };
  // Paid = expenses ที่จ่ายแล้ว (SUCCESS) − เงินรอคืน (refund_items ยังไม่คืน) — กด "ได้รับคืนแล้ว" ยอดก็กลับมานับ
  // ใน Paid ตามเดิม ต้องตรงกับ getPaidValue ใน projectDashboard/formulas.js เสมอ
  const renderCost = (data) => {
    const fil = _?.filter(data?.expenses, (each) => each?.status === 'SUCCESS');
    const cost1 = _?.sumBy(fil, (e) => e?.price);
    return (cost1 || 0) - renderRefundPending(data);
  };

  const renderCostApprove = (data) => {
    // Expenses ที่ยังมี pending_items entry ผูกอยู่ (source_expense_id) ถูกนับผ่าน "AP Pending"
    // (renderPendingUnpaid) แทนแล้ว — ข้ามไม่นับซ้ำที่นี่ กันหักเงินคงเหลือสองรอบ ต้องแก้คู่กับ getExpectedValue
    // ใน projectDashboard/formulas.js เสมอ (ของซ้ำกันคนละไฟล์ ดู CLAUDE.md)
    const apPendingLinkedIds = new Set(
      (data?.pending_items || [])
        .filter((i) => i?.source_expense_id)
        .map((i) => String(i.source_expense_id)),
    );
    const fil = _?.filter(
      data?.expenses,
      (each) => each?.status !== 'SUCCESS'
        && each?.status !== 'REJECT'
        && !apPendingLinkedIds.has(String(each?._id || each?.id)),
    );
    const cost1 = _?.sumBy(fil, (e) => e?.price);
    return cost1;
  };

  const renderPendingUnpaid = (data) => {
    const fil = _?.filter(data?.pending_items, (each) => !each?.paid);
    return _?.sumBy(fil, (i) => i?.amount) || 0;
  };

  // ค่ามัดจำ/ค่าประกันที่ติดมากับ expense (นับเป็นต้นทุนไปแล้ว) แต่รอได้รับเงินคืน — ไม่ใช่ต้นทุนจริงของบริษัท จึงหักออกจาก Grand Total
  const renderRefundPending = (data) => {
    const fil = _?.filter(data?.refund_items, (each) => !each?.refunded);
    return _?.sumBy(fil, (i) => i?.amount) || 0;
  };

  // เงินรอคืนถูกหักอยู่ใน renderCost (Paid) แล้ว ไม่หักซ้ำตรงนี้
  const renderGrandTotal = (data) => {
    return (renderCost(data) || 0) + (renderCostApprove(data) || 0) + (renderPendingUnpaid(data) || 0);
  };

  const renderTotal = (data) => {
    return data?.cost - renderGrandTotal(data);
  };

  return (
    <div>
      {!hideToggleButton && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
          <Button
            size="small"
            variant="outlined"
            onClick={toggleAllRows}
            startIcon={allExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            disabled={toggleAllDisabled}
          >
            {allExpanded ? U('ปิดทั้งหมด', 'Collapse all') : U('ดูค่าใช้จ่ายทั้งหมด', 'View all expenses')}
          </Button>
        </Box>
      )}
      <TableContainer
        component={Paper}
        elevation={2}
        sx={{
          borderRadius: 2,
          maxWidth: 1158,
          mx: 'auto',
        }}
      >
          <Table
            size="small"
            sx={{
              tableLayout: 'fixed',
              width: '100%',
              '& > tbody > tr > .MuiTableCell-root': {
                fontSize: '0.7rem',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              },
            }}
          >
            <colgroup>
              {COL_WIDTHS.map((w, i) => (
                <col key={i} style={{ width: w }} />
              ))}
            </colgroup>
            <TableHead>
              <TableRow sx={{ '& .MuiTableCell-root': { bgcolor: '#1e3a5f', py: 1 } }}>
                <TableCell sx={{ px: 0.25 }} />
                <TableCell>
                  <Typography variant="caption" sx={HEAD_TXT_SX}>{U('ลำดับ', 'No')}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="caption" sx={HEAD_TXT_SX}>{U('รายการ', 'Order')}</Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="caption" sx={HEAD_TXT_SX}>{U('งบประมาณ', 'Budget')}</Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="caption" sx={HEAD_TXT_SX}>{U('จ่ายแล้ว', 'Paid')}</Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="caption" sx={HEAD_TXT_SX}>{U('คาดว่าจะจ่าย', 'Expected')}</Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="caption" sx={HEAD_TXT_SX}>{U('รอจ่าย', 'AP Pending')}</Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="caption" sx={HEAD_TXT_SX}>{U('รอเงินคืน', 'Refund')}</Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="caption" sx={HEAD_TXT_SX}>{U('ยอดรวม', 'Grand Total')}</Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="caption" sx={HEAD_TXT_SX}>{U('คงเหลือ', 'Remaining')}</Typography>
                </TableCell>
                <TableCell align="center">
                  <Typography variant="caption" sx={HEAD_TXT_SX}>{U('จัดการ', 'Actions')}</Typography>
                </TableCell>
              </TableRow>
              <TableRow sx={{ '& .MuiTableCell-root': { bgcolor: '#eef2f7', fontSize: '0.7rem' } }}>
                <TableCell />
                <TableCell />
                <TableCell />
                <TableCell align="right">
                  <Box component="span" sx={TOTAL_BADGE_SX}>
                    {_.sumBy(budget, (e) => e?.cost || 0)
                      .toFixed(2)
                      .replace(/\d(?=(\d{3})+\.)/g, '$&,')}
                  </Box>
                </TableCell>
                <TableCell align="right">
                  <Box component="span" sx={TOTAL_BADGE_SX}>
                    {_.sumBy(budget, (e) => renderCost(e) || 0)
                      .toFixed(2)
                      .replace(/\d(?=(\d{3})+\.)/g, '$&,')}
                  </Box>
                </TableCell>
                <TableCell align="right">
                  <Box component="span" sx={TOTAL_BADGE_SX}>
                    {_.sumBy(budget, (e) => renderCostApprove(e) || 0)
                      .toFixed(2)
                      .replace(/\d(?=(\d{3})+\.)/g, '$&,')}
                  </Box>
                </TableCell>
                <TableCell align="right">
                  <Box component="span" sx={TOTAL_BADGE_SX}>
                    {_.sumBy(budget, (e) => renderPendingUnpaid(e) || 0)
                      .toFixed(2)
                      .replace(/\d(?=(\d{3})+\.)/g, '$&,')}
                  </Box>
                </TableCell>
                <TableCell align="right">
                  {(() => {
                    const refundTotal = _.sumBy(budget, (e) => renderRefundPending(e) || 0);
                    return (
                      <Box component="span" sx={TOTAL_BADGE_SX}>
                        {refundTotal > 0 ? '-' : ''}
                        {refundTotal.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,')}
                      </Box>
                    );
                  })()}
                </TableCell>
                <TableCell align="right">
                  <Box component="span" sx={TOTAL_BADGE_SX}>
                    {_.sumBy(budget, (e) => renderGrandTotal(e) || 0)
                      .toFixed(2)
                      .replace(/\d(?=(\d{3})+\.)/g, '$&,')}
                  </Box>
                </TableCell>
                <TableCell align="right">
                  {(() => {
                    const total = _.sumBy(budget, (e) => renderTotal(e) || 0);
                    return (
                      <Box
                        component="span"
                        sx={{
                          ...TOTAL_BADGE_SX,
                          ...(total < 0 && {
                            bgcolor: '#fef2f2',
                            border: '1px solid #fecaca',
                            color: '#d32f2f',
                          }),
                        }}
                      >
                        {total.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,')}
                      </Box>
                    );
                  })()}
                </TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {_.isEmpty(budget) ? (
                <TableRow>
                  <TableCell colSpan={11} align="center" sx={{ py: 3 }}>
                    <Typography variant="body2" color="text.secondary">{U('ไม่มีรายการงบประมาณ', 'No budgets')}</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                budget.map((e) => {
                  const rowBold = expandedRows[e.id] ? 700 : 400;
                  return (
                  <React.Fragment key={e.id}>
                    <TableRow
                      hover
                      sx={{ cursor: hasExpandableRows(e) ? 'pointer' : 'default' }}
                      onClick={() => hasExpandableRows(e) && toggleRow(e.id)}
                    >
                      <TableCell sx={{ px: 0.25 }}>
                        {hasExpandableRows(e) && (
                          <Tooltip title={expandedRows[e.id] ? 'ซ่อนค่าใช้จ่าย' : 'แสดงค่าใช้จ่าย'}>
                            <IconButton size="small">
                              <KeyboardArrowDownIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell sx={{ pl: 0.5 }}>
                        <Typography variant="caption" noWrap sx={{ display: 'block', fontSize: 'inherit' }}>
                          {e?.prefix}
                          {e?.budget_number}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ fontWeight: rowBold }}>
                        <Tooltip title={e?.name || ''}>
                          <Typography
                            variant="caption"
                            noWrap
                            sx={{ display: 'block', fontSize: 'inherit', fontWeight: 'inherit' }}
                          >
                            {e?.name}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: rowBold }}>
                        {e?.cost?.toFixed(2)?.replace(/\d(?=(\d{3})+\.)/g, '$&,')}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: rowBold }}>
                        {renderCost(e)
                          ?.toFixed(2)
                          ?.replace(/\d(?=(\d{3})+\.)/g, '$&,')}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: rowBold }}>
                        {renderCostApprove(e)
                          ?.toFixed(2)
                          ?.replace(/\d(?=(\d{3})+\.)/g, '$&,')}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ fontWeight: rowBold, color: renderPendingUnpaid(e) > 0 ? '#ed6c02' : 'inherit' }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                          <span
                            style={{ cursor: 'pointer' }}
                            onClick={(ev) => { ev.stopPropagation(); openPendingModal(e.id); }}
                          >
                            {renderPendingUnpaid(e)
                              .toFixed(2)
                              .replace(/\d(?=(\d{3})+\.)/g, '$&,')}
                          </span>
                          <Tooltip title="รายการเงินที่รอจ่าย">
                            <IconButton
                              aria-label="pending payment"
                              size="small"
                              color="warning"
                              onClick={(ev) => { ev.stopPropagation(); openPendingModal(e.id); }}
                            >
                              <Badge badgeContent={_.filter(e?.pending_items, (i) => !i?.paid).length} color="warning">
                                <HourglassEmptyIcon fontSize="inherit" />
                              </Badge>
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ fontWeight: rowBold, color: renderRefundPending(e) > 0 ? '#d32f2f' : 'inherit' }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                          <span
                            style={{ cursor: 'pointer' }}
                            onClick={(ev) => { ev.stopPropagation(); openRefundModal(e.id); }}
                          >
                            {renderRefundPending(e) > 0 ? '-' : ''}
                            {renderRefundPending(e)
                              .toFixed(2)
                              .replace(/\d(?=(\d{3})+\.)/g, '$&,')}
                          </span>
                          <Tooltip title="รายการเงินที่รอรับคืน">
                            <IconButton
                              aria-label="refund pending"
                              size="small"
                              color="info"
                              onClick={(ev) => { ev.stopPropagation(); openRefundModal(e.id); }}
                            >
                              <Badge badgeContent={_.filter(e?.refund_items, (i) => !i?.refunded).length} color="info">
                                <AssignmentReturnIcon fontSize="inherit" />
                              </Badge>
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: rowBold }}>
                        {renderGrandTotal(e)
                          .toFixed(2)
                          .replace(/\d(?=(\d{3})+\.)/g, '$&,')}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: rowBold, color: renderTotal(e) < 0 ? '#d32f2f' : 'inherit' }}>
                        {renderTotal(e)
                          ?.toFixed(2)
                          ?.replace(/\d(?=(\d{3})+\.)/g, '$&,')}
                      </TableCell>
                      <TableCell onClick={(ev) => ev.stopPropagation()}>
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 0.5 }}>
                          <Tooltip title="แก้ไข">
                            <IconButton
                              aria-label="edit"
                              size="small"
                              onClick={() => handleEditBudget(e)}
                              sx={{ p: 0.25 }}
                            >
                              <ModeEditIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="ลบ">
                            <IconButton
                              aria-label="delete"
                              size="small"
                              onClick={() => handleDeleteBudget(e)}
                              sx={{ p: 0.25 }}
                            >
                              <DeleteIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>

                    {/* Expanded expense rows */}
                    <TableRow>
                      <TableCell colSpan={11} sx={{ p: 0, border: 0, width: 0 }}>
                        <Collapse in={!!expandedRows[e.id]} timeout="auto" unmountOnExit>
                          <Box sx={{ mx: 2, my: 1, width: 0, minWidth: '100%', overflowX: 'auto' }}>
                            <Table
                              size="small"
                              sx={{ tableLayout: 'fixed', width: 'calc(90% - 15px)', ml: 'auto', mr: '50px' }}
                            >
                              <TableBody
                                sx={{
                                  '& tr:last-child .MuiTableCell-root': { borderBottom: 'none' },
                                }}
                              >
                                {_.isEmpty(e?.expenses) && _.isEmpty(e?.refund_expenses) ? (
                                  <TableRow>
                                    <TableCell colSpan={6} align="center">
                                      <Typography variant="caption" color="text.secondary">ไม่มีค่าใช้จ่าย</Typography>
                                    </TableCell>
                                  </TableRow>
                                ) : (
                                  (() => {
                                    const sortedExpenses = getSortedExpenses(e?.expenses, e.id);
                                    const expensesTotal = _.sumBy(
                                      e?.expenses,
                                      (exp) => (exp?.status === 'REJECT' ? 0 : (exp?.price || 0)),
                                    );
                                    return sortedExpenses.map((exp, i) => {
                                      // "รอจ่าย" = ยังไม่จ่ายสำเร็จ แต่ไม่ใช่ถูกปฏิเสธ/cancel (REJECT มีกากบาทแดงบอกอยู่แล้ว ไม่ต้องทำสีซ้ำ)
                                      const isPendingPay = exp?.status !== 'SUCCESS' && exp?.status !== 'REJECT';
                                      const rowColor = isPendingPay ? 'magenta' : 'inherit';
                                      return (
                                      <TableRow
                                        key={exp?._id || exp?.id}
                                        hover
                                        onDoubleClick={() => onEditExpense?.(exp?._id || exp?.id)}
                                        sx={{ cursor: onEditExpense ? 'pointer' : 'default' }}
                                      >
                                        <TableCell sx={{ width: 80, verticalAlign: 'top' }}>
                                          <Typography variant="caption" sx={{ whiteSpace: 'nowrap', color: rowColor }}>
                                            {exp?.date ? dayjs(exp.date).format('DD/MM/YYYY') : '-'}
                                          </Typography>
                                        </TableCell>
                                        <TableCell sx={{ width: 110, verticalAlign: 'top' }}>
                                          <Typography
                                            variant="caption"
                                            sx={{ fontFamily: 'monospace', color: isPendingPay ? 'magenta' : 'text.secondary' }}
                                          >
                                            {exp?.code || '-'}
                                          </Typography>
                                        </TableCell>
                                        <TableCell sx={{ verticalAlign: 'top' }}>
                                          <Typography
                                            variant="caption"
                                            sx={{ display: 'block', whiteSpace: 'normal', wordBreak: 'break-word', color: rowColor }}
                                          >
                                            {exp?.name || '-'}
                                          </Typography>
                                        </TableCell>
                                        <TableCell align="right" sx={{ width: 110, verticalAlign: 'top' }}>
                                          <Typography variant="caption" sx={{ color: rowColor }}>
                                            {exp?.price?.toFixed(2)?.replace(/\d(?=(\d{3})+\.)/g, '$&,') || '0.00'}
                                          </Typography>
                                        </TableCell>
                                        <TableCell align="center" sx={{ width: 32, verticalAlign: 'top' }}>
                                          {expenseStatusIcon(exp?.status)}
                                        </TableCell>
                                        {i === 0 && (
                                          <TableCell
                                            rowSpan={sortedExpenses.length}
                                            align="center"
                                            sx={{ width: 90, verticalAlign: 'middle' }}
                                          >
                                            <Typography
                                              variant="caption"
                                              fontWeight={700}
                                              sx={{ whiteSpace: 'nowrap' }}
                                            >
                                              {expensesTotal.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,')}
                                            </Typography>
                                          </TableCell>
                                        )}
                                      </TableRow>
                                      );
                                    });
                                  })()
                                )}
                                {/* รายการเบิกรอรับคืน (Expenses.isRefund, backend แยกมาไว้ refund_expenses) — แสดงอย่างเดียว
                                    ไม่นับรวมในยอด/คอลัมน์ Paid-Expected (ตัวเลขคิดจาก refund_items ในคอลัมน์ Refund อยู่แล้ว) */}
                                {!_.isEmpty(e?.refund_expenses) && (
                                  <>
                                    <TableRow>
                                      <TableCell colSpan={6} sx={{ pt: 1.25, pb: 0.25 }}>
                                        <Typography variant="caption" sx={{ fontWeight: 700, color: '#2e7d32' }}>
                                          รายการรอรับคืน (ไม่นับรวมในยอด)
                                        </Typography>
                                      </TableCell>
                                    </TableRow>
                                    {_.orderBy(e.refund_expenses, ['createdAt'], ['desc']).map((rf) => {
                                      const received = rf?.status === 'SUCCESS';
                                      return (
                                        <TableRow
                                          key={rf?._id || rf?.id}
                                          hover
                                          onDoubleClick={() => openRefundModal(e.id)}
                                          sx={{ cursor: 'pointer', bgcolor: 'rgba(46,125,50,0.04)' }}
                                        >
                                          <TableCell sx={{ width: 80, verticalAlign: 'top' }}>
                                            <Chip
                                              size="small"
                                              label={received ? 'คืนแล้ว' : 'รอคืน'}
                                              sx={{
                                                height: 18,
                                                fontSize: '0.65rem',
                                                fontWeight: 700,
                                                color: received ? '#2e7d32' : '#0288d1',
                                                bgcolor: received ? 'rgba(46,125,50,0.12)' : 'rgba(2,136,209,0.12)',
                                              }}
                                            />
                                          </TableCell>
                                          <TableCell sx={{ width: 110, verticalAlign: 'top' }}>
                                            <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
                                              {rf?.code || '-'}
                                            </Typography>
                                          </TableCell>
                                          <TableCell sx={{ verticalAlign: 'top' }}>
                                            <Typography
                                              variant="caption"
                                              sx={{ display: 'block', whiteSpace: 'normal', wordBreak: 'break-word', color: '#2e7d32' }}
                                            >
                                              {rf?.name || '-'}
                                            </Typography>
                                          </TableCell>
                                          <TableCell align="right" sx={{ width: 110, verticalAlign: 'top' }}>
                                            <Typography variant="caption" sx={{ color: '#2e7d32', fontWeight: 700 }}>
                                              -{(Number(rf?.price) || 0).toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,')}
                                            </Typography>
                                          </TableCell>
                                          <TableCell align="center" sx={{ width: 32, verticalAlign: 'top' }}>
                                            {received ? (
                                              <Tooltip title="ได้รับคืนแล้ว">
                                                <CheckCircleIcon fontSize="small" sx={{ color: 'success.main', verticalAlign: 'middle' }} />
                                              </Tooltip>
                                            ) : (
                                              <Tooltip title="รอรับคืน">
                                                <AssignmentReturnIcon fontSize="small" sx={{ color: 'info.main', verticalAlign: 'middle' }} />
                                              </Tooltip>
                                            )}
                                          </TableCell>
                                          <TableCell sx={{ width: 90 }} />
                                        </TableRow>
                                      );
                                    })}
                                  </>
                                )}
                              </TableBody>
                            </Table>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                  );
                })
              )}
            </TableBody>
          </Table>
      </TableContainer>

      <PendingItemsModal
        open={!!pendingModalId}
        budget={pendingModalBudget}
        onClose={closePendingModal}
      />

      <RefundItemsModal
        open={!!refundModalId}
        budget={refundModalBudget}
        onClose={closeRefundModal}
        onUpdated={() => onRefundUpdated?.()}
      />
    </div>
  );
});

export default BudgetTable;
